import React, { FC, useCallback } from 'react'
import { BtSelect, BtInputBase as BtInput } from '@linqpal/components/src/ui'
import { observer } from 'mobx-react'
import { useEditorModel } from '../../useEditorModel'
import { useTranslation } from 'react-i18next'
import { CompProps } from '../../Editor'
import { Spacer } from '../../../../../ui/atoms'
import { Instance, types } from 'mobx-state-tree'
import { editableModel } from '@linqpal/models'

const otherOption = 'Other'

const options = [
  'Sole Proprietorship',
  'Partnership',
  'LLC',
  'Corporation (S or C)',
  otherOption,
]

const BusinessTypeModel = types.model('BusinessType', {
  selectedType: '',
  other: '',
})

type BusinessTypeModelType = Instance<typeof BusinessTypeModel>

const BusinessTypeStore = types
  .compose(BusinessTypeModel, editableModel())
  .named('BusinessTypeStore')

const BusinessTypeStep: FC<CompProps> = ({
  doc,
  item,
  group,
  review,
  onValueUpdate,
}) => {
  const { t } = useTranslation('application')

  const validate = useCallback((value: BusinessTypeModelType | null) => {
    if (value?.selectedType === otherOption) {
      return !!value?.other
    }

    return !!value?.selectedType
  }, [])

  const model = useEditorModel<BusinessTypeModelType>(
    doc,
    group,
    item,
    onValueUpdate,
    BusinessTypeStore.create({ selectedType: '', other: '' }),
    validate,
    BusinessTypeModel,
  )

  const handleSelect = (value: string) => {
    model.setValue(
      'value',
      BusinessTypeStore.create({
        selectedType: value,
        other: value === otherOption ? model?.value?.other : '',
      }),
    )
  }

  const handleOtherChange = (value: string) => {
    model.setValue(
      'value',
      BusinessTypeStore.create({
        selectedType: model?.value?.selectedType,
        other: value,
      }),
    )
  }

  return (
    <>
      <Spacer height={review ? 24 : 18} />
      <BtSelect
        size="large"
        options={options}
        testID="BusinessTypeInput"
        label={t('Business.TypeLabel')}
        value={model?.value?.selectedType}
        placeholder={t('Business.TypePlaceholder')}
        onChange={handleSelect}
        primitiveArray
      />

      {model?.value?.selectedType === otherOption && (
        <>
          <Spacer height={review ? 24 : 18} />
          <BtInput
            size="large"
            value={model?.value?.other}
            label={t('Business.OtherTypeLabel')}
            onChangeText={handleOtherChange}
          />
        </>
      )}
    </>
  )
}

export default {
  title: 'Business.Type',
  component: observer(BusinessTypeStep),
}
